<script setup lang="ts">
import type { IconName } from "../Icon/icons";
import { computed } from "vue";
import VIcon from "../Icon/VIcon.vue";
import VTooltip from "../Tooltip/VTooltip.vue";

defineOptions({
  name: "VTableToolbarButton",
  // Attributes such as data-testid belong on the button, not on the tooltip around it.
  inheritAttrs: false,
});

/**
 * A button in the toolbar of a VTable: a 32px pill, the height of the search field next to it. `active` marks a
 * filter that narrows the list (light green), `open` the button whose panel is open. Without text it is a round icon
 * button; give it a `label`, which is also its tooltip.
 */
const {
  icon = undefined,
  label,
  active = false,
  open = false,
  disabled = false,
  loading = false,
  badge = null,
  iconOnly = false,
  haspopup = undefined,
  large = false,
} = defineProps<{
  icon?: IconName
  /**
   * Accessible name. For an icon button also the tooltip. A button with text shows its slot; the label then starts
   * with that text and may add to it ("Filters, 2 actief").
   */
  label: string
  active?: boolean
  open?: boolean
  disabled?: boolean
  loading?: boolean
  /** Number in a green bubble after the text, such as the number of active filters. */
  badge?: number | null
  iconOnly?: boolean
  haspopup?: "dialog" | "menu" | "listbox"
  /** 44px touch target, for phones. */
  large?: boolean
}>();

const emit = defineEmits<{
  click: [event: MouseEvent]
}>();

const slots = defineSlots<{
  default?: () => unknown
  /** An icon of its own (inline SVG) instead of `icon`. */
  icon?: () => unknown
}>();

const buttonClass = computed(() => [
  large ? "h-11 min-w-11" : "h-8 min-w-8",
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border text-[13px] leading-4 font-medium transition-colors",
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500",
  "disabled:cursor-default disabled:opacity-50",
  iconOnly ? (large ? "w-11 px-0" : "w-8 px-0") : (large ? "px-3.5" : "px-3"),
  badge !== null && badge > 0 && !iconOnly ? "pr-1.5" : undefined,
  active
    ? "border-primary-100 bg-primary-50 text-primary-900 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
    : "border-gray-300 bg-white text-gray-800 hover:border-primary-300 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-200",
  open ? "border-primary-500! ring-1 ring-inset ring-primary-500" : undefined,
]);
</script>

<template>
  <VTooltip :content="label" :disabled="!iconOnly" placement="top">
    <template #reference>
      <button
        type="button"
        :class="buttonClass"
        :disabled="disabled || loading"
        :aria-label="label"
        :aria-haspopup="haspopup"
        :aria-expanded="haspopup ? open : undefined"
        :aria-busy="loading || undefined"
        v-bind="$attrs"
        @click="(event: MouseEvent) => emit('click', event)"
      >
        <VIcon
          v-if="loading"
          name="loader-circle"
          class="animate-spin text-sm text-primary-500 motion-reduce:animate-none"
          aria-hidden="true"
        />
        <span v-else-if="slots.icon" class="inline-flex text-sm" aria-hidden="true">
          <slot name="icon" />
        </span>
        <VIcon
          v-else-if="icon"
          :name="icon"
          class="text-sm"
          aria-hidden="true"
        />
        <slot />
        <span
          v-if="badge !== null && badge > 0 && !iconOnly"
          class="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary-500 px-[5px] text-[11px] font-semibold leading-none text-white"
          aria-hidden="true"
        >
          {{ badge }}
        </span>
      </button>
    </template>
  </VTooltip>
</template>
